<template>
  <transition name="slide">
    <div v-if="isVisible" class="sidebarSlide position-absolute start-0 top-0 w-100 bg-white z-2">
      <dl class="list-unstyled px-3 py-3">
        <li
          class="py-3 my-3 mx-4 px-4 text-black cursor-pointer fw-semibold"
          v-if="isClientProspect"
          @click="
            router.push({
              name: signUpProspect
            })
          "
        >
          Registrate
        </li>
        <li class="py-3 my-3 mx-4 px-4 text-black cursor-pointer fw-semibold" v-if="!isRoutePublic">
          <label for="username">Usuario:</label>
          {{ userName }}
          <span
            class="float-end"
            v-if="
              getBalanceLogicState &&
              (agreementData?.agreementType === 1 || !agreementData?.agreementType)
            "
          >
            <span class="me-3">Saldo de bono:</span>
            <span class="float-end pulse-animation">{{
              remainingBalance < 0 ? 0 : $formatcurrency(remainingBalance)
            }}</span>
          </span>
          <span
            @click="openInfoAgreement"
            class="float-end"
            v-if="getBalanceLogicState && agreementData?.agreementType === 2"
          >
            <font-awesome-icon icon="fa-solid fa-circle-info" class="me-2 cursor-pointer" />
            <span class="me-3">Bono compra mínima:</span>
            <strong class="float-end" id="compraMinima">
              {{ $formatcurrency(agreementData?.agreement_minimum_purchase) }}
            </strong>
          </span>
        </li>
        <CategoriesComponent v-if="!isRoutePublic && !getBalanceLogicState && !isSeller" />
        <li
          v-if="!isRoutePublic && !isSeller"
          class="py-3 my-3 mx-4 px-4 text-black cursor-pointer optionHover"
          @click="goToRoute('home')"
          :class="{
            'text-white bg-primary-app fw-medium opacity-75': route?.name === 'home',
            'fw-light': route?.name !== 'home'
          }"
        >
          Tienda
        </li>
        <li
          v-if="!isRoutePublic && !isSeller"
          class="py-3 my-3 mx-4 px-4 text-black cursor-pointer optionHover"
          @click="goToRoute('shoppingCart')"
          :class="{
            'text-white bg-primary-app fw-medium opacity-75': route?.name === 'shoppingCart',
            'fw-light': route?.name !== 'shoppingCart'
          }"
        >
          Carrito
        </li>
        <li
          v-if="!isRoutePublic"
          class="py-3 my-3 mx-4 px-4 text-black cursor-pointer fw-light optionHover"
          @click="logout"
        >
          Salir
        </li>
      </dl>
    </div>
  </transition>
</template>

<script setup>
import CategoriesComponent from '@/components/layout/navigation/CategoriesComponent.vue'
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSidebarStore } from '@/stores/ui/sidebar'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { useShoppingStore } from '@/stores/shopping'
import Swal from 'sweetalert2'

const route = useRoute()
const router = useRouter()
const publicRoutesRequiredValidations = [
  'product_detail_public',
  'product_detail_only_list_pack_public',
  'category_public'
]
const shoppingStore = useShoppingStore()
const sidebarStore = useSidebarStore()
const userStore = useUserStore()
const authStore = useAuthStore()
const agreementData = computed(() => userStore.getAgreementData)
const remainingBalance = computed(() => shoppingStore.getUserBalanceMinusTotal)
const isClientProspect = computed(() => userStore.isClientProspect)
const isSeller = computed(() => userStore.isSeller)
const getBalanceLogicState = computed(() => shoppingStore.getBalanceLogicState)

const isVisible = computed({
  get: () => sidebarStore.isSidebarVisibleStore,
  set: () => {
    sidebarStore.toggleSidebarStore()
  }
})

const isRoutePublic = computed(() => publicRoutesRequiredValidations.includes(route.name))

const userName = computed(() => {
  if (!userStore.userInfo.name) return '---'
  return userStore.userInfo.name.substring(0, 40)
})

watch(isVisible, (newVal) => {
  if (newVal) {
    document.body.scrollTop = 0 // Para Safari
    document.documentElement.scrollTop = 0 // Para Chrome, Firefox, IE y Opera
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
    console.log('Overflow después de cerrar sidebar:', document.body.style.overflow)
  }
})

function logout() {
  authStore.logout()
}

function goToRoute(routeName) {
  if (isVisible.value) {
    isVisible.value = false
  }
  router.push({
    name: routeName
  })
}
const openInfoAgreement = () => {
  Swal.fire({
    title: 'Información Importante sobre el Bono en Productos del Evento',
    html: `
      <div class="text-left">
        <p>¡Buenas noticias! Esta sección especial de la tienda, identificada como <strong>"Bono"</strong>, te ofrece un beneficio adicional en tus compras.</p>
        <hr class="my-2">
        <p><strong>¿Cómo obtener el bono?</strong></p>
        <ul class="list-unstyled">
          <li class="mb-2">Debes incluir en tu carrito de compra <strong>únicamente productos</strong> que se encuentren dentro de la sección <strong>"Bono"</strong>.</li>
          <li>El total de tu compra de estos productos debe ser igual o superior a <strong>$${agreementData?.value.agreement_minimum_purchase}</strong>.</li>
        </ul>
        <hr class="my-2">
        <p><strong>Tu beneficio:</strong></p>
        <p>Si cumples con las condiciones anteriores, recibirás un <strong>bono</strong>.</p>
        <hr class="my-2">
        <p><strong>Importante:</strong></p>
        <ul class="list-unstyled">
          <li class="mb-2">Este bono se aplicará <strong>después de que completes tu compra</strong>.</li>
          <li class="mb-2">Una vez realizada tu compra, el bono será <strong>validado por nuestro equipo administrativo</strong>.</li>
        </ul>
        <hr class="my-2">
        <p class="text-center">¡Aprovecha este beneficio en los productos descatalogados!</p>
      </div>
    `,
    icon: 'info',
    width: '70vw',
    showCloseButton: true,
    showCancelButton: false,
    confirmButtonColor: '#f16322',
    confirmButtonText: 'Entendido, ¡a comprar!',
    cancelButtonText: 'Cancelar' // Aunque no haya acción de cancelar aquí, se puede dejar por consistencia
  })
}
</script>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.5s;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}
</style>
